import React from 'react';
import classnames from 'classnames/bind';
import { DateTime } from 'luxon';

import { FilesWidget as CurrentFilesWidget } from 'types/dashboard_widget';
import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { isNotNullOrUndefined } from 'services/nullable';
import { useCameras } from 'cameras/hooks/useCameras';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDashboardFilterParams } from 'dashboards/hooks/useDashboardFilterParams';
import { FileSearchMetadata } from 'files/components/FileSearchMetadata';
import { useFilesCursorWithSearchMetadata } from 'files/hooks/useFilesWithSearchMetadata';
import { useCursorPagination } from 'components/Pagination/hooks/useCursorPagination';
import { useModal } from 'hooks/useModal';
import { useStreams } from 'streams/hooks/useStreams';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTrackableNames } from 'trackables/hooks';

import { CursorPagination } from 'components/Pagination/CursorPagination';
import { Icon } from 'components/Icon/IconDeprecated';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { TagsList } from 'tags/components/TagsList/TagsList';
import { Text } from 'components/Text/Text';
import { Thumbnail } from 'components/Thumbnail/Thumbnail';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import { FilesWidgetSkeleton } from './Skeleton';
import styles from './Files.module.scss';

const c = classnames.bind(styles);

export function FilesWidget(widget: CurrentFilesWidget) {
  const { options = {} } = widget;

  const { gateway_id, stream_ids = [] } = useDashboardFilterParams();

  const { start, stop, timezone } = useTimeRangeParams();

  const { data: currentPlan } = useCurrentPlan();

  const { open } = useModal();

  const { cursorParams, reset, getProps } = useCursorPagination({ limit: 50 });

  const streamIdsKey = stream_ids.join(',');
  const optionsKey = JSON.stringify(options);

  React.useEffect(() => {
    reset();
  }, [reset, start, stop, gateway_id, streamIdsKey, optionsKey]);

  const gatewayIdsFilter = gateway_id
    ? Array.from(new Set([gateway_id, ...(options.gateway_ids || [])]))
    : options.gateway_ids;

  const streamIdsFilter = Array.from(
    new Set([...stream_ids, ...(options.stream_ids || [])])
  );

  const {
    data: files,
    isFetching,
    isPreviousData,
    isLoading: isLoadingFiles,
  } = useFilesCursorWithSearchMetadata({
    params: {
      created_ts_since: start,
      created_ts_until: stop,
      with_thumbnail: true,
      include_snapshots: false,
      include_search_metadata: currentPlan?.can_search,
      is_recording: true,
      ...cursorParams,
      ...options,
      gateway_ids: gatewayIdsFilter,
      stream_ids: streamIdsFilter.length ? streamIdsFilter : undefined,
    },
    refetchInterval: WIDGET_REFETCH_INTERVAL,
    keepPreviousData: true,
  });

  const paginationProps = getProps(files, isFetching);

  const streamIds = [
    ...new Set(
      files?.data.map((file) => file.stream_id).filter(isNotNullOrUndefined)
    ),
  ];

  const { data: streams, isInitialLoading: isLoadingStreams } = useStreams({
    params: {
      stream_ids: streamIds,
      limit: streamIds.length,
    },
    enabled: streamIds.length > 0,
  });

  const streamsCamerasMap = streams
    ? Object.fromEntries(
        streams?.data
          .filter(({ camera_id }) => camera_id)
          .map(({ id, camera_id }) => [id, camera_id])
      )
    : {};

  const cameraIds = [
    ...new Set(Object.values(streamsCamerasMap).filter(isNotNullOrUndefined)),
  ];

  const { data: cameras, isInitialLoading: isLoadingCameras } = useCameras({
    params: { camera_ids: cameraIds, limit: cameraIds.length },
    enabled: cameraIds.length > 0,
  });

  const isLoading = isLoadingFiles || isLoadingStreams || isLoadingCameras;
  const hasFiles = files && files.data.length > 0;
  const showLoading =
    isLoading || (isFetching && !hasFiles) || (isFetching && isPreviousData);
  const trackableIds = React.useMemo(() => {
    if (!files?.data) {
      return [];
    }

    const ids = new Set<string>();
    for (const file of files.data) {
      for (const id of file.trackables ?? []) {
        if (id) {
          ids.add(id);
        }
      }
    }

    return Array.from(ids);
  }, [files?.data]);
  const trackableNamesQuery = useTrackableNames(trackableIds);
  const trackableItemsLookup = trackableNamesQuery.data?.items;
  const trackableListsLookup = trackableNamesQuery.data?.lists;

  return (
    <Widget widget={widget}>
      <ul className={c('files')}>
        {!hasFiles && !isLoading && !isFetching && (
          <EmptyView>No files for this time period and filters.</EmptyView>
        )}

        {showLoading ? (
          <SkeletonList
            min={hasFiles ? files?.data.length : 5}
            max={hasFiles ? files?.data.length : 5}
            component={<FilesWidgetSkeleton />}
          />
        ) : (
          <>
            {files?.data.map((file) => {
              const cameraId =
                file.stream_id && streamsCamerasMap[file.stream_id];
              const isFromCameraStream = Boolean(file.stream_id && cameraId);
              const source = isFromCameraStream
                ? cameras?.data.find(({ id }) => id === cameraId)
                : streams?.data.find(({ id }) => id === file.stream_id);

              return (
                <li key={file.id}>
                  <button
                    className={c('file', 'border')}
                    onClick={() =>
                      open('files-search', {
                        defaultFile: {
                          id: file.id,
                          created_at: file.created_at,
                        },
                        defaultStreamId: file.stream_id,
                      })
                    }
                  >
                    <Thumbnail
                      thumbnail_url={file.thumbnail_url}
                      mime_type={file.mime_type}
                    />
                    <div className={c('info')}>
                      <Text size="small" inline>
                        {DateTime.fromISO(file.created_at)
                          .setZone(timezone)
                          .toLocaleString(DateTime.DATETIME_MED)}
                      </Text>

                      <div className={c('entities')}>
                        {source && (
                          <Text className={c('text')} inline>
                            <Icon
                              name={isFromCameraStream ? 'camera' : 'stream'}
                            />
                            <span className={c('text-inner')}>
                              {source.name}
                            </span>
                          </Text>
                        )}

                        {file.description && (
                          <Text className={c('text')} inline>
                            <Icon name="meta" />
                            <span className={c('text-inner')}>
                              {file.description}
                            </span>
                          </Text>
                        )}
                      </div>

                      <FileSearchMetadata
                        layout="compact"
                        maxObjectIcons={6}
                        objects={file.objects}
                        events={file.events}
                        texts={file.texts}
                        trackables={file.trackables}
                        trackableItemsLookup={trackableItemsLookup}
                        trackableListsLookup={trackableListsLookup}
                      />
                      <TagsList defaultValue={file.tags} readOnly />
                    </div>
                  </button>
                </li>
              );
            })}
          </>
        )}

        <CursorPagination
          className={c('pagination')}
          {...paginationProps}
        />
      </ul>
    </Widget>
  );
}
